// ─────────────────────────────────────────────────────────────────────────────
// Navs Art Studio — Blog content store
// ─────────────────────────────────────────────────────────────────────────────
// HOW TO ADD A NEW POST
// ─────────────────────────────────────────────────────────────────────────────
// 1. Add a new object to the POSTS array below with:
//      slug         — URL segment, kebab-case (must be unique)
//      title        — H1 title
//      excerpt      — 1–2 sentence preview shown on cards
//      category     — must be one of CATEGORIES below
//      author       — string (defaults to "Navs Art Studio")
//      publishedAt  — ISO date string (YYYY-MM-DD)
//      readingTime  — "5 min read" style label
//      coverImage   — hero banner URL (1600px+)
//      thumbnail    — card image URL (800px is fine)
//      featured     — boolean, only one post should be true
//      content      — ordered array of blocks (see RenderBlogContent.jsx)
//
//   Block types supported:
//      { type: 'p',     text: '...'                       }   paragraph
//      { type: 'h2',    text: '...'                       }   section heading
//      { type: 'h3',    text: '...'                       }   sub-section
//      { type: 'list',  items: ['..', '..'], emoji: '✨'  }   emoji bullet list
//      { type: 'check', items: ['..', '..']               }   checkmark list
//      { type: 'quote', text: '...'                       }   pull-quote
//
// 2. (Future) Replace this file with a real CMS or `/api/posts` endpoint —
//    Blog.jsx and BlogPost.jsx read from this module's helpers (getAllPosts,
//    getPostBySlug, getFeaturedPost), so wiring an API is a one-spot swap.
// ─────────────────────────────────────────────────────────────────────────────

export const CATEGORIES = [
  { key: 'home-decor',       label: 'Home Décor',          emoji: '🏠' },
  { key: 'personalized-gifts', label: 'Personalized Gifts', emoji: '🎁' },
  { key: 'ocean-inspiration', label: 'Ocean Inspiration',   emoji: '🌊' },
  { key: 'behind-the-scenes', label: 'Behind The Scenes',   emoji: '✨' },
  { key: 'custom-stories',   label: 'Custom Stories',       emoji: '❤️' },
];

const POSTS = [
  {
    slug: 'how-custom-artwork-adds-value-to-home-decor',
    title: 'How Custom Artwork Adds Value to Home Décor',
    excerpt:
      'Furniture fills a room, but custom artwork gives it personality — adding emotion, mood, and the kind of meaning a house needs to truly feel like home.',
    category: 'home-decor',
    secondaryCategory: 'Custom Artwork',
    author: 'Navs Art Studio',
    publishedAt: '2026-05-27',
    readingTime: '6 min read',
    coverImage:
      'https://images.unsplash.com/photo-1618220179428-22790b461013?w=1920&q=80&auto=format&fit=crop',
    thumbnail:
      'https://images.unsplash.com/photo-1618220179428-22790b461013?w=900&q=80&auto=format&fit=crop',
    content: [
      { type: 'p', text: 'Your home should feel like you.' },
      {
        type: 'p',
        text:
          'Furniture fills a room, but artwork gives it personality. It adds emotion, creates a mood, and turns empty walls into something meaningful. While mass-produced décor can help complete a space, custom artwork creates a connection that makes a house truly feel like home.',
      },
      {
        type: 'p',
        text:
          'Custom artwork goes beyond decoration — it tells stories, reflects personality, and creates spaces people remember.',
      },

      { type: 'h2', text: 'Why Artwork Matters in a Home' },
      {
        type: 'p',
        text:
          'Walk into a room with plain walls and then walk into a room with carefully selected artwork.',
      },
      { type: 'p', text: 'The difference is immediate.' },
      { type: 'p', text: 'Artwork changes how a room feels. It can make a space feel:' },
      {
        type: 'list',
        emoji: '✨',
        items: [
          'Warm and inviting',
          'Calm and relaxing',
          'Luxurious and modern',
          'Personal and meaningful',
          'Bright and energetic',
        ],
      },
      {
        type: 'p',
        text:
          "The right artwork doesn't just sit on a wall — it becomes part of the experience of being in that space.",
      },

      { type: 'h2', text: '1. Custom Artwork Makes Your Space Personal' },
      {
        type: 'p',
        text:
          'Anyone can buy décor from a store, but custom artwork is different because it reflects your story.',
      },
      { type: 'h3', text: 'Examples:' },
      {
        type: 'list',
        items: [
          { emoji: '🌊', text: 'Ocean-inspired designs that remind you of your favorite beach' },
          { emoji: '🚗', text: 'Car artwork inspired by your passion' },
          { emoji: '❤️', text: 'Anniversary pieces celebrating meaningful moments' },
          { emoji: '🏠', text: 'Personalized home décor that fits your style' },
          { emoji: '👨‍👩‍👧', text: 'Family-inspired artwork that holds memories' },
        ],
      },
      {
        type: 'p',
        text:
          'When guests walk into your home, they immediately notice details that feel unique rather than generic.',
      },
      { type: 'p', text: 'Your space begins to tell a story.' },

      { type: 'h2', text: '2. Artwork Creates a Focal Point' },
      { type: 'p', text: 'Every room needs something that naturally catches attention.' },
      {
        type: 'p',
        text:
          'Custom artwork often becomes the first thing people notice when they enter a room.',
      },
      {
        type: 'p',
        text:
          'A single statement piece can completely transform a room without changing furniture or renovating the space.',
      },

      { type: 'h2', text: '3. Custom Artwork Creates Emotional Connection' },
      { type: 'p', text: "People don't remember rooms because of furniture." },
      { type: 'p', text: 'They remember how a room made them feel.' },
      {
        type: 'p',
        text:
          'When artwork connects to experiences or memories, it becomes more than décor.',
      },
      { type: 'p', text: 'It becomes part of your life story.' },

      { type: 'h2', text: '4. Artwork Helps Tie a Room Together' },
      { type: 'p', text: 'Custom artwork can help bring together:' },
      {
        type: 'check',
        items: [
          'Wall colors',
          'Furniture tones',
          'Textures',
          'Decorative accents',
          'Overall room mood',
        ],
      },
      { type: 'p', text: 'Ocean-inspired blues can create a calm feeling.' },
      { type: 'p', text: 'Neutral colors create elegance.' },
      { type: 'p', text: 'Bold colors create energy and statement pieces.' },
      {
        type: 'p',
        text: 'Artwork often becomes the final detail that makes a room feel complete.',
      },

      { type: 'h2', text: '5. Handmade Artwork Creates Something One-of-a-Kind' },
      { type: 'p', text: 'One of the most beautiful parts of handmade artwork is uniqueness.' },
      { type: 'p', text: 'No two handcrafted pieces are exactly identical.' },
      {
        type: 'p',
        text:
          'Small variations in texture, movement, and detail create character that cannot be mass-produced.',
      },

      { type: 'h2', text: 'How To Choose Artwork For Your Space' },
      {
        type: 'check',
        items: [
          'Match artwork with room colors',
          'Think about room mood',
          'Consider wall size',
          'Choose meaningful themes',
          'Pick artwork that reflects personality',
        ],
      },

      { type: 'h2', text: 'Final Thoughts' },
      { type: 'p', text: 'Home décor should do more than fill walls.' },
      { type: 'p', text: 'It should create a feeling.' },
      {
        type: 'p',
        text:
          'Custom artwork adds personality, tells stories, creates emotional connection, and transforms ordinary spaces into something memorable.',
      },
      {
        type: 'p',
        text:
          "Because when people walk into your home, they shouldn't just see a room.",
      },
      { type: 'p', text: 'They should feel you in it.' },
    ],
  },

  // ── Coming-soon placeholders for the upcoming editorial calendar ──
  {
    slug: 'what-buyers-really-consider-when-choosing-custom-artwork-vancouver',
    title:
      'What Buyers Really Consider When Choosing Custom Artwork for Their Home in Vancouver',
    metaTitle:
      'Custom Artwork Vancouver | Personalized Resin & Car Wall Art | Navs Art Studio',
    metaDescription:
      'Discover handcrafted, one-of-a-kind custom artwork in Vancouver. From ocean-inspired resin art to personalized car wall pieces, Navs Art Studio elevates your home décor with unique designs.',
    excerpt:
      'From emotion and personality to interior fit and craftsmanship — the real questions Vancouver homeowners ask themselves before commissioning a piece of custom artwork.',
    category: 'home-decor',
    secondaryCategory: 'Custom Artwork',
    author: 'Navs Art Studio',
    publishedAt: '2026-07-18',
    readingTime: '5 min read',
    coverImage:
      'https://customer-assets-agu9un31.emergentagent.net/job_cd7911c0-2a95-487c-9654-01df6f4c012d/artifacts/ns3t51rt_ChatGPT%20Image%20Jun%205%2C%202026%2C%2002_16_54%20PM%202.PNG',
    thumbnail:
      'https://customer-assets-agu9un31.emergentagent.net/job_cd7911c0-2a95-487c-9654-01df6f4c012d/artifacts/ns3t51rt_ChatGPT%20Image%20Jun%205%2C%202026%2C%2002_16_54%20PM%202.PNG',
    coverPosition: 'center 30%',
    keywords: [
      'custom artwork Vancouver',
      'resin art Vancouver',
      'custom car wall art',
      'personalized home décor',
      'handmade artwork Canada',
      'ocean resin art',
    ],
    featured: true,
    content: [
      {
        type: 'p',
        text:
          'Choosing artwork for your home is rarely just about filling a wall. In Vancouver — a city shaped by ocean views, mountain light, and a design-forward mindset — buyers approach custom art with more intention than ever. They ask deeper questions before committing to a piece, and the answers usually go far beyond aesthetics.',
      },
      {
        type: 'p',
        text:
          'Below are the five things buyers really think about when commissioning a custom piece from Navs Art Studio — and why each one matters.',
      },

      { type: 'h2', text: '1. Emotional Connection in Home Décor' },
      {
        type: 'p',
        text:
          'Selecting custom artwork in Vancouver starts with emotion. Buyers first ask themselves, "How does this piece make me feel?" Art has the power to evoke calm, inspire energy, or create nostalgia. Our handcrafted resin and custom car artworks are designed to connect emotionally, ensuring your home décor is not only beautiful but also meaningful.',
      },
      {
        type: 'quote',
        text:
          'The pieces buyers keep coming back to aren\u2019t the loudest — they\u2019re the ones that quietly match how a room is meant to feel.',
      },

      { type: 'h2', text: '2. Personalized Expression Through Art' },
      {
        type: 'p',
        text:
          'Art allows homeowners to reflect their personality and life experiences. Our custom artwork services in Vancouver empower clients to bring their visions to life — whether it\u2019s an ocean-inspired piece or a personalized car wall art creation — making each piece unique and meaningful.',
      },
      {
        type: 'list',
        items: [
          { emoji: '🌊', text: 'Ocean-inspired resin pieces built around a favourite coastline' },
          { emoji: '🚗', text: 'Custom car wall art that honours a lifelong passion' },
          { emoji: '❤️', text: 'Anniversary and milestone pieces made to hold a memory' },
          { emoji: '🎨', text: 'Colour palettes chosen to match your exact interior tones' },
        ],
      },

      {
        type: 'image',
        src:
          'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1600&q=80&auto=format&fit=crop',
        alt:
          'Modern Vancouver living room styled with custom ocean-inspired wall art above the sofa',
        caption:
          'A statement resin piece can anchor the entire mood of a Vancouver living room.',
      },

      { type: 'h2', text: '3. Aesthetic Fit for Interiors' },
      {
        type: 'p',
        text:
          'Homeowners often consider how art complements their interior style, colour palette, and room layout. Navs Art Studio\u2019s diverse collections enhance modern, contemporary, and eclectic interiors, ensuring your home décor looks cohesive and elevated.',
      },
      { type: 'h3', text: 'The three questions worth asking before you commit:' },
      {
        type: 'check',
        items: [
          'Does the palette complement my walls, flooring, and largest furniture pieces?',
          'Is the scale right for the wall — not too small, not overwhelming?',
          'Does the mood match the room\u2019s purpose (calm bedroom vs. energetic living space)?',
        ],
      },

      { type: 'h2', text: '4. Longevity and Craftsmanship' },
      {
        type: 'p',
        text:
          'Quality and longevity matter. Each custom artwork in Vancouver is meticulously handcrafted using premium materials and layering techniques. Every detail is considered so that your piece remains visually stunning and cherished for years to come.',
      },
      {
        type: 'list',
        emoji: '✨',
        items: [
          'Museum-grade resin poured in thin, deliberate layers',
          'UV-stable pigments to preserve depth and colour over time',
          'Sealed, finished edges for a gallery-ready presentation',
          'Signed, one-of-a-kind — no reprints, ever',
        ],
      },

      { type: 'h2', text: '5. Why Custom Art Adds Value' },
      {
        type: 'p',
        text:
          'Custom art transforms a house into a home, reflecting the individuality of its inhabitants. With personalized pieces, your walls become a gallery of memories, passions, and personal style, making your décor both exclusive and memorable.',
      },
      {
        type: 'p',
        text:
          'And in a city like Vancouver — where design tastes lean elevated but deeply personal — a custom piece is one of the few investments in your home that truly cannot be replicated anywhere else.',
      },

      { type: 'h2', text: 'Ready to Bring a Story to Your Walls?' },
      {
        type: 'p',
        text:
          'Explore our collections or design a personalized piece today. Let Navs Art Studio turn your home into a gallery that tells your story.',
      },

      {
        type: 'engagement',
        title: 'Top 3 Reader Engagement Questions',
        items: [
          'What inspires you most when selecting artwork for your home?',
          'If you could create a custom piece, what personal memory or theme would you like captured?',
          'Which of our collections — ocean, cars, or custom — is your favourite and why?',
        ],
      },
    ],
  },

  // ── Coming-soon placeholders for the upcoming editorial calendar ──
  {
    slug: 'how-to-choose-wall-art-for-your-living-room',
    title: 'How to Choose Wall Art for Your Living Room',
    excerpt:
      'Scale, color, height, and mood — the four guideposts professional designers use when curating statement walls.',
    category: 'home-decor',
    author: 'Navs Art Studio',
    publishedAt: '2026-03-04',
    readingTime: '5 min read',
    thumbnail:
      'https://customer-assets.emergentagent.com/job_navs-art-studio/artifacts/pk46nxgj_IMG_5015.jpeg',
    comingSoon: true,
  },
  {
    slug: 'ocean-inspired-home-decor-ideas',
    title: 'Ocean-Inspired Home Décor Ideas',
    excerpt:
      'Bring coastal calm into any room with palette, texture, and one focal resin piece that anchors the space.',
    category: 'ocean-inspiration',
    author: 'Navs Art Studio',
    publishedAt: '2026-03-18',
    readingTime: '4 min read',
    thumbnail:
      'https://customer-assets.emergentagent.com/job_d42f1bf2-eb5e-4681-9fcb-1be42bf600c3/artifacts/i48bb4l4_IMG_6083.jpeg',
    comingSoon: true,
  },
  {
    slug: 'personalized-gift-ideas-for-every-occasion',
    title: 'Personalized Gift Ideas for Every Occasion',
    excerpt:
      'From anniversaries to housewarmings — thoughtful custom artwork ideas for the moments that matter most.',
    category: 'personalized-gifts',
    author: 'Navs Art Studio',
    publishedAt: '2026-04-01',
    readingTime: '6 min read',
    thumbnail:
      'https://customer-assets.emergentagent.com/job_d42f1bf2-eb5e-4681-9fcb-1be42bf600c3/artifacts/yebg53s9_IMG_6027%202.jpeg',
    comingSoon: true,
  },
  {
    slug: 'behind-the-scenes-how-resin-artwork-comes-to-life',
    title: 'Behind the Scenes: How Resin Artwork Comes to Life',
    excerpt:
      'From first pour to final cure — a peek into the slow, careful process of bringing a resin piece to life.',
    category: 'behind-the-scenes',
    author: 'Navs Art Studio',
    publishedAt: '2026-04-15',
    readingTime: '7 min read',
    thumbnail:
      'https://customer-assets.emergentagent.com/job_navs-art-studio/artifacts/j2uwmz8f_68BB0444-5D6B-4E65-94A6-BFF19BB84356.JPG',
    comingSoon: true,
  },
];

// ── Helpers (read-only API for the rest of the app) ─────────────────────────
export const getAllPosts = () =>
  [...POSTS].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));

export const getPublishedPosts = () =>
  getAllPosts().filter((p) => !p.comingSoon);

export const getFeaturedPost = () =>
  POSTS.find((p) => p.featured) || getAllPosts()[0];

export const getPostBySlug = (slug) => POSTS.find((p) => p.slug === slug);

export const getCategoryByKey = (key) =>
  CATEGORIES.find((c) => c.key === key) || null;

export const getRelatedPosts = (slug, limit = 4) =>
  getPublishedPosts().filter((p) => p.slug !== slug).slice(0, limit);

export default POSTS;
